import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext.jsx';
import { FaCog, FaCalendarAlt, FaFileAlt, FaChartBar } from 'react-icons/fa';
import PayrollSetup from './Payroll/PayrollSetup.jsx';
import DeductionCalendar from './Payroll/DeductionCalendar.jsx';
import DeductionReport from './Payroll/DeductionReport.jsx';
import PayrollReport from './Payroll/PayrollReport.jsx';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminAuthToken') || localStorage.getItem('kioskAuthToken') || localStorage.getItem('authToken')}`,
});

function Payroll() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  
  // Navigation state
  const [activeSection, setActiveSection] = useState('setup');
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Message states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data states
  const [staff, setStaff] = useState([]);
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [deductionData, setDeductionData] = useState([]);
  
  const sidebarItems = [
    { id: 'setup', icon: FaCog, label: 'Payroll Setup' },
    { id: 'calendar', icon: FaCalendarAlt, label: 'Deduction Calendar' },
    { id: 'deductions', icon: FaFileAlt, label: 'Deduction Report' },
    { id: 'reports', icon: FaChartBar, label: 'Payroll Report' },
  ];

  // Authentication check
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Initialize data
  useEffect(() => {
    if (user) {
      fetchInitialData();
    }
  }, [user]);

  // Clear messages after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // API Functions - Aligned with ultra-thin backend routes
  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [staffRes, settingsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/staff`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/api/payroll/settings`, { headers: getAuthHeaders() })
      ]);
      
      setStaff(staffRes.data.staff || staffRes.data || []);
      setPayrollSettings(settingsRes.data);
      
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load payroll data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStaffData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/staff`, {
        headers: getAuthHeaders(),
      });
      setStaff(response.data.staff || response.data || []);
      return response.data.staff || response.data || [];
    } catch (err) {
      console.error('Error fetching staff:', err);
      setError('Failed to fetch staff data');
      return [];
    }
  }, []);

  const updatePayrollSettings = useCallback(async (settings) => {
    try {
      setActionLoading(true);
      const response = await axios.put(`${API_BASE_URL}/api/payroll/settings`, settings, {
        headers: getAuthHeaders()
      });
      setPayrollSettings(response.data);
      setSuccess('Payroll settings updated successfully!');
      return response.data;
    } catch (err) {
      console.error('Error updating payroll settings:', err);
      setError('Failed to update settings: ' + (err.response?.data?.message || err.message));
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  const updateStaffPayroll = useCallback(async (staffId, payrollData) => {
    try {
      setActionLoading(true);
      await axios.put(`${API_BASE_URL}/api/payroll/staff-setup/${staffId}`, payrollData, {
        headers: getAuthHeaders()
      });
      
      // Update local state
      setStaff(prevStaff => 
        prevStaff.map(s => 
          s._id === staffId ? { ...s, ...payrollData } : s
        )
      );
      
      setSuccess('Staff payroll updated successfully!');
    } catch (err) {
      console.error('Error updating staff payroll:', err);
      setError('Failed to update staff payroll: ' + (err.response?.data?.message || err.message));
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // UPDATED: Simplified to match ultra-thin backend routes
  const fetchDeductionData = useCallback(async (params) => {
    try {
      setLoading(true);
      console.log('Fetching deduction data with params:', params);
      
      const response = await axios.get(`${API_BASE_URL}/api/payroll/deductions`, {
        headers: getAuthHeaders(),
        params
      });
      
      if (response.data.deductionsData) {
        setDeductionData(response.data.deductionsData);
        return response.data;
      }
      
      return response.data;
    } catch (err) {
      console.error('Error fetching deduction data:', err);
      
      // Handle specific backend errors
      if (err.response?.status === 404) {
        const errorMsg = err.response.data.message || 'No data found for requested period';
        setError(errorMsg);
        return { deductionsData: [], available: false, message: errorMsg };
      }
      
      setError('Failed to fetch deduction data: ' + (err.response?.data?.message || err.message));
      return { deductionsData: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  // UPDATED: Simplified to match ultra-thin backend routes
  const generatePayrollReport = useCallback(async (params) => {
    try {
      setLoading(true);
      console.log('Generating payroll report with params:', params);
      
      const response = await axios.post(`${API_BASE_URL}/api/payroll/generate-report`, params, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (err) {
      console.error('Error generating payroll report:', err);
      
      // Handle specific backend errors
      if (err.response?.status === 404) {
        const errorMsg = err.response.data.message || 'No data found for requested period';
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      
      setError('Failed to generate payroll report: ' + (err.response?.data?.message || err.message));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper functions
  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  // Don't render if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Payroll Management</h1>
              <p className="text-gray-600">Shift based dynamic per-minute payroll</p>
            </div>
            <div className="flex items-center gap-3">
              {loading && (
                <div className="flex items-center gap-2 text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">Loading...</span>
                </div>
              )}
              <div className="text-xs text-gray-500 bg-white px-3 py-2 rounded-lg border">
                {staff.length} staff members
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
            <button 
              onClick={clearMessages}
              className="text-red-600 hover:text-red-800 ml-4"
            >
              ×
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>{success}</span>
            </div>
            <button 
              onClick={clearMessages}
              className="text-green-600 hover:text-green-800 ml-4"
            >
              ×
            </button>
          </div>
        )}

        {/* Action Loading Overlay */}
        {actionLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span>Processing...</span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="mb-8 sticky top-0 z-10 bg-white shadow-lg rounded-lg">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex space-x-4">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeSection === item.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="text-lg" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {activeSection === 'setup' && (
            <PayrollSetup
              staff={staff}
              payrollSettings={payrollSettings}
              loading={loading}
              actionLoading={actionLoading}
              updatePayrollSettings={updatePayrollSettings}
              updateStaffPayroll={updateStaffPayroll}
              fetchStaffData={fetchStaffData}
              setError={setError}
              setSuccess={setSuccess}
            />
          )}

          {activeSection === 'calendar' && (
            <DeductionCalendar
              staff={staff}
              loading={loading}
              fetchDeductionData={fetchDeductionData}
              setError={setError}
              setSuccess={setSuccess}
            />
          )}

          {activeSection === 'deductions' && (
            <DeductionReport
              staff={staff}
              deductionData={deductionData}
              loading={loading}
              fetchDeductionData={fetchDeductionData}
              setError={setError}
              setSuccess={setSuccess}
            />
          )}

          {activeSection === 'reports' && (
            <PayrollReport
              staff={staff}
              loading={loading}
              generatePayrollReport={generatePayrollReport}
              setError={setError}
              setSuccess={setSuccess}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Payroll;